// saveBase — the save id the editor's buffer is currently based on.
//
// Set to the tip we LOADED (on open) and the tip we CREATED (on each save).
// Sent as `previousSaveId` on the next save so the backend can detect a
// concurrent save (someone else advanced the tip) and reply 409 STALE_BASE,
// which the editor turns into a reconcile (take-theirs / keep-mine) instead of
// silently forking the save history. Null ⇒ send nothing ⇒ old last-write-wins
// behaviour (safe fallback, no false conflicts). See RECONCILIATION-STRATEGY §6.1.
//
// KEYED BY VERSION. A version owns its code, so it owns its base: the whole
// point of the id above is to name the save THIS buffer came from, and two
// versions have two different answers. The draft store has been version-scoped
// since versions shipped; this was not, so it held one slot for the whole app
// and every switch overwrote the outgoing version's answer with the incoming
// one's. That is fine while a switch is the only thing writing — it re-points
// the base on the way in — but anything resolving ACROSS a switch wrote the
// version you just left onto the one you just opened: a save landing mid-switch
// stamps its saveId as the new version's base, and the next save on that
// version declares a base from the wrong lane.
//
// Reads and writes without an explicit scope use the current one, so callers
// that have no idea versions exist (open-context, travel, boot) keep working
// unchanged. Callers that start async work should capture the scope first and
// pass it back on completion — see `setBaseSaveIdFor`.

/** Slot for "no version scope declared" — apps that predate versions, and the
 *  window before the version list has resolved on boot. */
const UNSCOPED = ' unscoped';

const bases = new Map<string, string | null>();
let currentScope: string = UNSCOPED;

/**
 * The save the BUFFER currently holds — deliberately global, because there is
 * one buffer. Not the same question as the per-version base above, and keeping
 * them apart matters:
 *
 *   base[v]        "what are version v's edits based on?"  → previousSaveId
 *   loadedSaveId   "what is materialized right now?"       → do we need to travel?
 *
 * One slot answered both while the base was global, because it always tracked
 * the last thing loaded. Once the base became per-version that stopped being
 * true, and a checkout comparing against the TARGET's base found it already
 * equal to that version's own saveId — so it skipped the travel and the editor
 * kept showing the version you had just left.
 */
let loadedSaveId: string | null = null;

function key(scope?: string | null): string {
  if (scope === undefined) return currentScope;
  return scope ?? UNSCOPED;
}

/**
 * Point subsequent unscoped reads/writes at `versionId`. Called by the version
 * switch alongside the draft store's own scope change, so the two stay in step.
 *
 * Does NOT clear the incoming version's base: switching back to a version it
 * has already resolved should find the answer it left there.
 */
export function setBaseVersionScope(versionId: string | null): void {
  currentScope = key(versionId);
}

/**
 * The scope writes currently land in, as an OPAQUE token. Capture before
 * starting async work; hand back to `setBaseSaveIdFor` to write into it later,
 * or to `restoreBaseVersionScope` to make it current again.
 *
 * Opaque so callers never have to know how "no version" is spelled — passing a
 * captured token back through `setBaseVersionScope` would otherwise turn the
 * unscoped slot into a version literally named after the sentinel.
 */
export function getBaseVersionScope(): string {
  return currentScope;
}

/** Put a token from `getBaseVersionScope` back in force — for a switch that
 *  failed partway and must leave the previous version's scope intact. */
export function restoreBaseVersionScope(token: string): void {
  currentScope = token;
}

/**
 * Declare the buffer's base. Every caller of this reaches it having just put a
 * known save INTO the buffer — boot, an open-context load, a checkout, a
 * travel — so it records the loaded save too, and existing callers stay correct
 * without knowing versions exist.
 *
 * The one caller that must not do this is a save whose response landed after
 * the user switched away: its saveId is that version's base but is not what is
 * on screen. That path uses `setBaseSaveIdFor`.
 */
export function setBaseSaveId(id: string | null): void {
  bases.set(currentScope, id);
  loadedSaveId = id;
}

/** What the buffer holds. Null when unknown (a materialized branch with no save
 *  row, or right after something replaced the buffer with unsaved content). */
export function getLoadedSaveId(): string | null {
  return loadedSaveId;
}

/** Record that the buffer now holds `id`, without touching any version's base. */
export function setLoadedSaveId(id: string | null): void {
  loadedSaveId = id;
}

/**
 * Write the base for a specific scope, whether or not it is still the current
 * one. For work that began under one version and finished under another — a
 * save whose response arrives after the user has switched away. Its saveId is
 * the truth for the version it was saved against, and nothing at all for the
 * one now open.
 */
export function setBaseSaveIdFor(scope: string, id: string | null): void {
  bases.set(scope, id);
}

export function getBaseSaveId(scope?: string | null): string | null {
  return bases.get(key(scope)) ?? null;
}

/**
 * Forget a version's base entirely — distinct from setting it to null, which is
 * a declared "this buffer has no base, save last-write-wins". Used when a
 * version stops existing (archived) so its slot cannot be resurrected by a
 * later id collision.
 */
export function clearBaseSaveId(scope?: string | null): void {
  bases.delete(key(scope));
}
